import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { isString } from "lodash";
import cn from "classnames";

import "assets/fontawesome/js/solid";
import "assets/fontawesome/js/regular";
import "assets/fontawesome/js/brands";
import "assets/fontawesome/js/light";
import "assets/fontawesome/js/fontawesome";

export const Icon = (props: IconProps) => {
    const { className, icon, iconVersion = 1 } = props;

    if (isString(icon) && isSocialIcon(icon.toString())) {
        if (iconVersion > 1) {
            return <SocialIcon {...props} />;
        }

        return <FontAwesomeIcon {...props} className={cn("icon", className)} icon={["fab", icon as IconName]} />;
    }

    if (!icon) {
        return null;
    }

    return <FontAwesomeIcon {...props} className={cn("icon", className)} />;
};

interface IconProps extends FontAwesomeIconProps {
    iconVersion?: number;
}

const SocialIcon = (props: FontAwesomeIconProps) => {
    const { icon } = props;

    if (icon === "facebook-f") {
        return <FacebookIcon {...props} />;
    }

    if (icon === "twitter") {
        return <TwitterIcon {...props} />;
    }

    if (icon === "linkedin") {
        return <LinkedinIcon {...props} />;
    }

    if (icon === "youtube") {
        return <YoutubeIcon {...props} />;
    }

    if (icon === "instagram") {
        return <InstagramIcon {...props} />;
    }

    return null;
};

const TwitterIcon = ({ className, title }: SocialIconProps) => (
    <svg className={cn("icon", className)} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        {title && <title>{title}</title>}
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M 3.429 0 C 1.537 0 0 1.538 0 3.429 L 0 20.571 C 0 22.462 1.537 24 3.429 24 L 20.571 24 C 22.463 24 24 22.462 24 20.571 L 24 3.429 C 24 1.538 22.463 0 20.571 0 L 3.429 0 Z M 19.345 4.5 L 13.784 10.854 L 20.325 19.5 L 15.204 19.5 L 11.196 14.255 L 6.605 19.5 L 4.061 19.5 L 10.007 12.702 L 3.734 4.5 L 8.984 4.5 L 12.611 9.295 L 16.8 4.5 L 19.345 4.5 Z M 17.32 17.979 L 8.218 5.941 L 6.702 5.941 L 15.905 17.979 L 17.314 17.979 L 17.32 17.979 Z"
            fill="currentColor"
        />
    </svg>
);

const YoutubeIcon = ({ className, title }: SocialIconProps) => (
    <svg className={cn("icon", className)} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        {title && <title>{title}</title>}
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.0678 0C22.6861 0 24 1.31391 24 2.93222V21.0678C24 22.6861 22.6861 24 21.0678 24H2.93222C1.31391 24 0 22.6861 0 21.0678V2.93222C0 1.31391 1.31386 0 2.93222 0L21.0678 0ZM20.085 8.23266C19.8909 7.50661 19.3192 6.93483 18.5931 6.74081C17.277 6.38812 12 6.38812 12 6.38812C12 6.38812 6.72305 6.38812 5.40689 6.74081C4.68098 6.93483 4.10911 7.50661 3.91505 8.23266C3.5625 9.54867 3.5625 12.2945 3.5625 12.2945C3.5625 12.2945 3.5625 15.0402 3.91505 16.3561C4.10911 17.0821 4.68098 17.6541 5.40689 17.8481C6.72305 18.2006 12 18.2006 12 18.2006C12 18.2006 17.277 18.2006 18.5931 17.8481C19.3192 17.6541 19.8909 17.0821 20.085 16.3561C20.4375 15.0402 20.4375 12.2945 20.4375 12.2945C20.4375 12.2945 20.4375 9.54867 20.085 8.23266ZM10.3124 14.8258V9.76317L14.6966 12.2945L10.3124 14.8258Z"
            fill="currentColor"
        />
    </svg>
);

const InstagramIcon = ({ className, title }: SocialIconProps) => (
    <svg className={cn("icon", className)} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        {title && <title>{title}</title>}
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.0678 0C22.6861 0 24 1.31391 24 2.93222V21.0678C24 22.6861 22.6861 24 21.0678 24H2.93222C1.31391 24 0 22.6861 0 21.0678V2.93222C0 1.31391 1.31386 0 2.93222 0L21.0678 0ZM12 3.79688C9.77217 3.79688 9.4928 3.8063 8.61788 3.84623C7.74473 3.88608 7.14844 4.02473 6.62663 4.22756C6.08719 4.43719 5.62973 4.71769 5.17369 5.17369C4.71764 5.62973 4.43714 6.08723 4.22752 6.62663C4.02473 7.14844 3.88608 7.74473 3.84619 8.61788C3.8063 9.4928 3.79688 9.77217 3.79688 12C3.79688 14.2278 3.8063 14.5072 3.84619 15.3821C3.88608 16.2553 4.02473 16.8516 4.22752 17.3734C4.43714 17.9128 4.71764 18.3703 5.17369 18.8263C5.62973 19.2824 6.08719 19.5629 6.62663 19.7725C7.14844 19.9753 7.74473 20.1139 8.61788 20.1538C9.4928 20.1937 9.77217 20.2031 12 20.2031C14.2278 20.2031 14.5072 20.1937 15.3821 20.1538C16.2553 20.1139 16.8516 19.9753 17.3734 19.7725C17.9128 19.5629 18.3703 19.2824 18.8263 18.8263C19.2824 18.3703 19.5629 17.9128 19.7725 17.3734C19.9753 16.8516 20.1139 16.2553 20.1538 15.3821C20.1937 14.5072 20.2031 14.2278 20.2031 12C20.2031 9.77217 20.1937 9.4928 20.1538 8.61788C20.1139 7.74473 19.9753 7.14844 19.7725 6.62663C19.5629 6.08723 19.2824 5.62973 18.8263 5.17369C18.3703 4.71769 17.9128 4.43719 17.3734 4.22756C16.8516 4.02473 16.2553 3.88608 15.3821 3.84623C14.5072 3.8063 14.2278 3.79688 12 3.79688ZM12 5.27494C14.1903 5.27494 14.4498 5.28328 15.3148 5.32275C16.1145 5.35927 16.5489 5.49291 16.838 5.60517C17.2209 5.754 17.4941 5.9318 17.7812 6.21881C18.0683 6.50588 18.246 6.77916 18.3948 7.16203C18.5071 7.45111 18.6408 7.88545 18.6772 8.68523C18.7167 9.55022 18.7251 9.80967 18.7251 12C18.7251 14.1903 18.7167 14.4498 18.6772 15.3148C18.6408 16.1145 18.5071 16.5489 18.3948 16.838C18.246 17.2209 18.0683 17.4941 17.7812 17.7812C17.4941 18.0683 17.2209 18.246 16.838 18.3948C16.5489 18.5071 16.1145 18.6408 15.3148 18.6772C14.4499 18.7167 14.1905 18.7251 12 18.7251C9.80953 18.7251 9.55013 18.7167 8.68523 18.6772C7.88545 18.6408 7.45111 18.5071 7.16203 18.3948C6.77911 18.246 6.50588 18.0683 6.21881 17.7812C5.93175 17.4941 5.75395 17.2209 5.60517 16.838C5.49286 16.5489 5.35922 16.1145 5.3227 15.3148C5.28323 14.4498 5.27489 14.1903 5.27489 12C5.27489 9.80967 5.28323 9.55022 5.3227 8.68523C5.35922 7.88545 5.49286 7.45111 5.60517 7.16203C5.75395 6.77916 5.93175 6.50588 6.21881 6.21881C6.50588 5.9318 6.77911 5.754 7.16203 5.60517C7.45111 5.49291 7.88545 5.35927 8.68523 5.32275C9.55022 5.28328 9.80967 5.27494 12 5.27494ZM12 7.78758C9.67355 7.78758 7.78758 9.67355 7.78758 12C7.78758 14.3265 9.67355 16.2124 12 16.2124C14.3265 16.2124 16.2124 14.3265 16.2124 12C16.2124 9.67355 14.3265 7.78758 12 7.78758ZM12 14.7344C10.4898 14.7344 9.26564 13.5102 9.26564 12C9.26564 10.4898 10.4898 9.26564 12 9.26564C13.5102 9.26564 14.7344 10.4898 14.7344 12C14.7344 13.5102 13.5102 14.7344 12 14.7344ZM17.3632 7.62117C17.3632 8.16483 16.9225 8.6055 16.3788 8.6055C15.8352 8.6055 15.3945 8.16483 15.3945 7.62117C15.3945 7.07752 15.8352 6.6368 16.3788 6.6368C16.9225 6.6368 17.3632 7.07752 17.3632 7.62117Z"
            fill="currentColor"
        />
    </svg>
);

const LinkedinIcon = ({ className, title }: SocialIconProps) => (
    <svg className={cn("icon", className)} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        {title && <title>{title}</title>}
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.0678 0C22.6861 0 24 1.31391 24 2.93222V21.0678C24 22.6861 22.6861 24 21.0678 24H2.93222C1.31391 24 0 22.6861 0 21.0678V2.93222C0 1.31391 1.31386 0 2.93222 0L21.0678 0ZM7.52161 19.8412V9.26067H4.00411V19.8412H7.52161ZM20.2031 19.8412V13.7737C20.2031 10.5237 18.4679 9.01186 16.154 9.01186C14.2882 9.01186 13.4524 10.038 12.9845 10.7587V9.26067H9.46786C9.5145 10.2535 9.46786 19.8412 9.46786 19.8412H12.9844V13.9323C12.9844 13.616 13.0072 13.2998 13.1004 13.0738C13.3542 12.4421 13.9332 11.7878 14.9048 11.7878C16.1768 11.7878 16.6864 12.7584 16.6864 14.1802V19.8412H20.2031ZM5.78662 4.15884C4.58316 4.15884 3.79688 4.95005 3.79688 5.98706C3.79688 7.00228 4.55925 7.81528 5.74008 7.81528H5.76277C6.98925 7.81528 7.7527 7.00228 7.7527 5.98706C7.72997 4.9515 6.99141 4.16109 5.78662 4.15884Z"
            fill="currentColor"
        />
    </svg>
);

const FacebookIcon = ({ className, title }: SocialIconProps) => (
    <svg className={cn("icon", className)} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        {title && <title>{title}</title>}
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.0678 0C22.6861 0 24 1.31391 24 2.93222V21.0678C24 22.6861 22.6861 24 21.0678 24H16.0771V14.9571H19.199L19.793 11.0843H16.0771V8.57095C16.0771 7.51144 16.5961 6.47864 18.2605 6.47864H19.95V3.18145C19.95 3.18145 18.4167 2.9198 16.9508 2.9198C13.8905 2.9198 11.8902 4.77459 11.8902 8.13248V11.0843H8.48836V14.9571H11.8902V24H2.93222C1.31391 24 0 22.6861 0 21.0678V2.93222C0 1.31391 1.31386 0 2.93222 0L21.0678 0Z"
            fill="currentColor"
        />
    </svg>
);

const isSocialIcon = (icon: string) => {
    return ["facebook-f", "twitter", "linkedin", "youtube", "instagram"].includes(icon);
};

interface SocialIconProps {
    className?: string;
    title?: string;
}
